import CloseIcon from "@mui/icons-material/Close";
import { Chip, Typography } from "@mui/material";
import { useField, useFormikContext } from "formik";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { v4 } from "uuid";

import { IconButton } from "@citrine/atoms/icon-button";
import { CatalystIcon } from "@citrine/atoms/icons";
import { Spinner } from "@citrine/atoms/loading";
import { useConfiguration } from "@citrine/resources/configuration";

import type { CatalystMode } from "#src/contexts/catalyst";
import { useCatalystContext } from "#src/contexts/catalyst";

import * as sx from "./catalyst.sx";
import { DocumentManagerButton } from "./document-manager-button";
import {
  useApplyAndRejectModelExpertChanges,
  useSubmitQuestion,
  useSwitchToModelExpertPromptHandlers,
} from "./hooks";
import { LiteratureExpertChip } from "./literature-expert-chip";
import { ModeSelector } from "./mode-selector";
import { QuestionsAndAnswers } from "./questions-and-answers";
import type {
  CatalystForm,
  CatalystQuestionAndAnswer,
  ModifiedConfigModelQuestionAndAnswer,
  PredictorChangeHandlers,
  SwitchToModelExpertPrompt,
  SwitchToModelExpertPromptHandlers,
} from "./types";
import {
  isModeChangeStatement,
  isModifiedConfigModelQuestionAndAnswer,
} from "./types";
import {
  TEST_IDS,
  isAnsweredOrErrored,
  isIdleSwitchToModelExpertPrompt,
  isResolved,
  isUnresolvedModifiedConfigModelQuestionAndAnswer,
} from "./utils";
import { WelcomeMessage } from "./welcome-message";

function CatalystFormView({
  disableNewQuestions,
  questionsAndAnswers,
  teamId,
  ...questionsAndAnswersProps
}: {
  disableNewQuestions?: boolean;
  questionsAndAnswers: CatalystQuestionAndAnswer[];
  teamId?: string;
} & PredictorChangeHandlers &
  SwitchToModelExpertPromptHandlers) {
  const { formatMessage } = useIntl();

  const [{ value: question }] = useField<CatalystForm["question"]>("question");
  const { isSubmitting, submitForm } = useFormikContext<CatalystForm>();
  const { enablePrivateDocuments } = useConfiguration();
  const onKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && question.trim().length && !disableNewQuestions) {
        await submitForm();
      }
    },
    [disableNewQuestions, question, submitForm],
  );

  const {
    expanded,
    mode,
    modelExpertEnabled,
    predictor,
    setMode,
    setExpanded,
  } = useCatalystContext();

  const questionsAndAnswersContainerRef = useRef<HTMLDivElement>(null);
  const questionInputRef = useRef<HTMLInputElement>(null);

  const numberOfAnsweredOrErroredQuestions = useMemo(
    () => questionsAndAnswers.filter(isAnsweredOrErrored).length,
    [questionsAndAnswers],
  );

  const numberOfResolvedQuestions = useMemo(
    () => questionsAndAnswers.filter(isResolved).length,
    [questionsAndAnswers],
  );

  // Scroll down to the bottom of the questions and answers container whenever
  // questions are answered or whenever an answer that requires a follow-up action
  // is resolved.
  useLayoutEffect(() => {
    const container = questionsAndAnswersContainerRef.current;
    if (container) {
      container.scrollTo(0, container.scrollHeight);
    }
  }, [numberOfAnsweredOrErroredQuestions, numberOfResolvedQuestions]);

  useEffect(() => {
    if (expanded) {
      questionInputRef.current?.focus();
    }
  }, [expanded]);

  return (
    <sx.Panel data-testid={TEST_IDS.drawerContent}>
      <sx.CardHeader
        title={
          <sx.TitleAndBadgeContainer>
            <CatalystIcon fontSize="large" />
            <Typography variant="h3">
              <FormattedMessage id="catalyst.title" />
            </Typography>
            <Chip
              label={formatMessage({ id: "Beta" })}
              variant="outlined"
              size="small"
            />
          </sx.TitleAndBadgeContainer>
        }
        action={
          <sx.ActionContainer>
            <sx.ModeAndDocumentManagerContainer>
              {modelExpertEnabled ? (
                <ModeSelector onChange={setMode} value={mode} />
              ) : (
                <LiteratureExpertChip />
              )}

              {enablePrivateDocuments ? (
                <DocumentManagerButton teamId={teamId} />
              ) : null}
            </sx.ModeAndDocumentManagerContainer>
            <IconButton
              data-fullstoryid="catalyst.close-button"
              size="small"
              onClick={() => setExpanded(false)}
            >
              <CloseIcon />
            </IconButton>
          </sx.ActionContainer>
        }
      />
      <sx.Content ref={questionsAndAnswersContainerRef}>
        {numberOfAnsweredOrErroredQuestions ? (
          <QuestionsAndAnswers
            data={questionsAndAnswers}
            hasPredictorWritePermissions={predictor?.hasWritePermissions}
            {...questionsAndAnswersProps}
          />
        ) : (
          <WelcomeMessage />
        )}
      </sx.Content>
      <sx.Container>
        <sx.Input
          autoComplete="off"
          disabled={disableNewQuestions}
          fullWidth
          inputRef={questionInputRef}
          multiline
          maxRows={5}
          name="question"
          onKeyDown={onKeyDown}
          placeholder={formatMessage({
            id: `catalyst.${mode}.question-placeholder`,
          })}
          slotProps={{
            htmlInput: {
              "data-testid": TEST_IDS.questionTextField,
            },
            input: {
              startAdornment: isSubmitting ? (
                <sx.SpinnerContainer>
                  <Spinner size={20} />
                </sx.SpinnerContainer>
              ) : (
                <sx.PromptIcon />
              ),
            },
          }}
          submitOnEnter
        />
      </sx.Container>
    </sx.Panel>
  );
}

/** `initialQuestionsAndAnswers` prop used for testing purposes */
export function Catalyst({
  teamId,
  initialQuestionsAndAnswers = [],
}: {
  teamId?: string;
  initialQuestionsAndAnswers?: CatalystQuestionAndAnswer[];
}) {
  const { formatMessage } = useIntl();

  const {
    expanded,
    mode,
    modelExpertEnabled,
    predictor,
    setExpanded,
    setMode,
    subscribe,
  } = useCatalystContext();

  const [questionsAndAnswers, setQuestionsAndAnswers] = useState(
    initialQuestionsAndAnswers,
  );

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, [setExpanded]);

  const onSubmit = useSubmitQuestion({
    mode,
    predictor,
    setQuestionsAndAnswers,
  });

  const applyAndRejectModelExpertChangeHandlers =
    useApplyAndRejectModelExpertChanges({
      predictor,
      setQuestionsAndAnswers,
    });

  const isApplyingModelChange = useMemo(
    () =>
      Boolean(
        questionsAndAnswers.find(
          (questionAndAnswer) =>
            isModifiedConfigModelQuestionAndAnswer(questionAndAnswer) &&
            questionAndAnswer.state === "applying",
        ),
      ),
    [questionsAndAnswers],
  );

  const switchToModelExpertPromptHandlers =
    useSwitchToModelExpertPromptHandlers({
      currentMode: mode,
      setMode,
      setQuestionsAndAnswers,
    });

  const hasIdleSwitchToModelExpertPrompt = useMemo(
    () => questionsAndAnswers.some(isIdleSwitchToModelExpertPrompt),
    [questionsAndAnswers],
  );

  const hasUnresolvedModelConfigQuestionAndAnswer = useMemo(
    () =>
      questionsAndAnswers.some(
        isUnresolvedModifiedConfigModelQuestionAndAnswer,
      ),
    [questionsAndAnswers],
  );

  // Use ref to avoid unsubscribing/resubscribing the mode/predictor observer
  // functions in the useEffects below
  const hasIdleSwitchToModelExpertPromptRef = useRef(
    hasIdleSwitchToModelExpertPrompt,
  );
  hasIdleSwitchToModelExpertPromptRef.current =
    hasIdleSwitchToModelExpertPrompt;

  const hasUnresolvedModelConfigQuestionAndAnswerRef = useRef(
    hasUnresolvedModelConfigQuestionAndAnswer,
  );
  hasUnresolvedModelConfigQuestionAndAnswerRef.current =
    hasUnresolvedModelConfigQuestionAndAnswer;

  useEffect(() => {
    if (!modelExpertEnabled && hasIdleSwitchToModelExpertPrompt) {
      setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
        prevQuestionsAndAnswers.map((questionAndAnswer) =>
          isIdleSwitchToModelExpertPrompt(questionAndAnswer)
            ? ({
                ...questionAndAnswer,
                automaticallyResolvedMessage: formatMessage({
                  id: "catalyst.use-model-expert-prompt.auto-resolve.predictor-builder-exited",
                }),
                state: "resolved",
              } satisfies SwitchToModelExpertPrompt)
            : questionAndAnswer,
        ),
      );
    }
  }, [
    formatMessage,
    hasIdleSwitchToModelExpertPrompt,
    mode,
    modelExpertEnabled,
  ]);

  const hasUsedModelExpertRef = useRef(false);
  hasUsedModelExpertRef.current ||= mode === "model-expert";
  const hasPromptedToSwitchToModelExpertRef = useRef(false);
  useEffect(() => {
    // Prompt user to switch to Model Expert if they open Catalyst when a predictor
    // is loaded and we haven't prompted them for this predictor before / haven't
    // used the Model Expert this session.
    if (
      expanded &&
      mode !== "model-expert" &&
      modelExpertEnabled &&
      !hasUsedModelExpertRef.current &&
      !hasPromptedToSwitchToModelExpertRef.current
    ) {
      hasPromptedToSwitchToModelExpertRef.current = true;
      setQuestionsAndAnswers((prevQuestionsAndAnswers) => [
        ...prevQuestionsAndAnswers,
        {
          type: "switch-to-model-expert-prompt",
          id: v4(),
          catalystMode: mode,
          state: "idle",
        },
      ]);
    }
  }, [expanded, mode, modelExpertEnabled]);

  const addModeChangeMessage = useCallback((newMode: CatalystMode) => {
    setQuestionsAndAnswers((prevQuestionsAndAnswers) => {
      // Avoid duplicate messages when mode and expand are toggled simultaneously
      const lastQA = prevQuestionsAndAnswers.at(-1);
      return isModeChangeStatement(lastQA) && lastQA.catalystMode === newMode
        ? prevQuestionsAndAnswers
        : [
            ...prevQuestionsAndAnswers,
            {
              type: "mode-change",
              catalystMode: newMode,
              id: v4(),
            },
          ];
    });
  }, []);

  const modeRef = useRef(mode);
  modeRef.current = mode;
  const lastVisibleModeRef = useRef(mode);
  useEffect(() => {
    return subscribe("expanded", (newExpanded) => {
      if (newExpanded) {
        // show a mode-change message if the mode is different from when panel was last closed
        if (modeRef.current !== lastVisibleModeRef.current) {
          addModeChangeMessage(modeRef.current);
        }
      } else {
        // on closing the panel, save a reference to the last mode seen
        lastVisibleModeRef.current = modeRef.current;
      }
    });
  }, [addModeChangeMessage, subscribe]);

  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;

  // If the Catalyst mode changes, add a ModeChangeStatement to the chat.
  // Resolve any unacted-upon Model Expert modified config answers if the
  // Catalyst mode is no longer Model Expert.
  // Also resolve any idle SwitchToModelExpertPrompts.
  useEffect(() => {
    return subscribe("mode", (newMode, prevMode) => {
      if (newMode !== prevMode) {
        if (expandedRef.current) {
          // show a mode-change message if the mode is changed while the panel is open
          addModeChangeMessage(newMode);
        }

        if (hasIdleSwitchToModelExpertPromptRef.current) {
          setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
            prevQuestionsAndAnswers.map((prevQuestionAndAnswer) =>
              isIdleSwitchToModelExpertPrompt(prevQuestionAndAnswer)
                ? ({
                    ...prevQuestionAndAnswer,
                    automaticallyResolvedMessage: formatMessage({
                      id: "catalyst.use-model-expert-prompt.auto-resolve.catalyst-mode-changed-elsewhere",
                    }),
                    state: "resolved",
                  } satisfies SwitchToModelExpertPrompt)
                : prevQuestionAndAnswer,
            ),
          );
        }

        if (
          newMode !== "model-expert" &&
          hasUnresolvedModelConfigQuestionAndAnswerRef.current
        ) {
          setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
            prevQuestionsAndAnswers.map((prevQuestionAndAnswer) =>
              isUnresolvedModifiedConfigModelQuestionAndAnswer(
                prevQuestionAndAnswer,
              )
                ? ({
                    ...prevQuestionAndAnswer,
                    automaticallyDiscardedMessage: formatMessage({
                      id: "catalyst.modified-config.auto-cancel.catalyst-mode-changed",
                    }),
                    state: "rejected",
                  } satisfies ModifiedConfigModelQuestionAndAnswer)
                : prevQuestionAndAnswer,
            ),
          );
        }
      }
    });
  }, [addModeChangeMessage, formatMessage, subscribe]);

  // If the predictor id/version are changed or the predictor state is unmounted,
  // resolve any unacted-upon Model Expert modified config answers.
  useEffect(() => {
    return subscribe("predictor", (newPredictor, oldPredictor) => {
      if (hasUnresolvedModelConfigQuestionAndAnswerRef.current) {
        const message = !newPredictor
          ? formatMessage({
              id: "catalyst.modified-config.auto-cancel.predictor-builder-exited",
            })
          : newPredictor.id !== oldPredictor?.id ||
              newPredictor.version !== oldPredictor?.version
            ? formatMessage({
                id: "catalyst.modified-config.auto-cancel.predictor-changed",
              })
            : undefined;

        if (message) {
          setQuestionsAndAnswers((prevQuestionsAndAnswers) =>
            prevQuestionsAndAnswers.map((prevQuestionAndAnswer) =>
              isUnresolvedModifiedConfigModelQuestionAndAnswer(
                prevQuestionAndAnswer,
              )
                ? ({
                    ...prevQuestionAndAnswer,
                    automaticallyDiscardedMessage: message,
                    state: "rejected",
                  } satisfies ModifiedConfigModelQuestionAndAnswer)
                : prevQuestionAndAnswer,
            ),
          );
        }
      }
    });
  }, [formatMessage, subscribe]);

  return (
    <>
      <sx.Catalyst<CatalystForm>
        className={expanded ? sx.CATALYST_EXPANDED_CLASS : undefined}
        initialValues={{
          question: "",
        }}
        onSubmit={onSubmit}
        submitOnEnter
      >
        <CatalystFormView
          // Disable new questions if a model config change is in progress or
          // if there are unacted-upon model config change answers.
          teamId={teamId}
          disableNewQuestions={
            hasIdleSwitchToModelExpertPrompt ||
            hasUnresolvedModelConfigQuestionAndAnswer ||
            isApplyingModelChange
          }
          questionsAndAnswers={questionsAndAnswers}
          {...applyAndRejectModelExpertChangeHandlers}
          {...switchToModelExpertPromptHandlers}
        />
      </sx.Catalyst>
      <sx.ExpandButton
        data-testid={TEST_IDS.openButton}
        className={expanded ? sx.CATALYST_EXPANDED_CLASS : undefined}
        data-fullstoryid="catalyst.open-button"
        onClick={toggleExpanded}
      >
        <sx.ButtonIcon />
      </sx.ExpandButton>
    </>
  );
}
